/**
 * HorseBasicDetailsCard — Basic Details edit card extracted from HorseOverview.
 * Props: horse, canEditAccount, onUpdate
 */
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Badge } from '../../ui/badge';
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from '../../ui/select';
import { Pencil } from 'lucide-react';
import { toast } from 'sonner';
import { horseAPI } from '../../../services/api';

const COLOR_LABELS = {
  bay: 'Bay', black: 'Black', chestnut: 'Chestnut',
  dark_bay_brown: 'Dark Bay/Brown', gray_roan: 'Gray/Roan',
  palomino: 'Palomino', white: 'White',
};

const HorseBasicDetailsCard = ({ horse, canEditAccount, onUpdate }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({});

  useEffect(() => {
    if (horse) {
      setForm({
        name: horse.name || '',
        system_name: horse.system_name || '',
        sex: horse.sex || '',
        color: horse.color || '',
        castrated: horse.castrated || false,
        cryptorchid: horse.cryptorchid || false,
        birthdate: horse.birthdate || '',
        still_nursing: horse.still_nursing || false,
        separated_from_dam: horse.separated_from_dam || false,
        graduate: horse.graduate || false,
        is_top_horse: horse.is_top_horse || false,
        microchip_id: horse.microchip_id || '',
      });
    }
  }, [horse?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const startEditing = () => {
    setForm({
      name: horse.name || '',
      birthdate: horse.birthdate ? horse.birthdate.split('T')[0] : '',
      jockey_club_registration: horse.jockey_club_registration || '',
      sex: horse.sex || 'male',
      color: horse.color || '',
      area_foaled: horse.area_foaled || '',
      microchip_id: horse.microchip_id || '',
      sire_name: horse.sire_name || '',
      dam_name: horse.dam_name || '',
      dam_year_foaled: horse.dam_year_foaled || '',
      still_nursing: horse.still_nursing || false,
      separated_from_dam: horse.separated_from_dam || false,
      castrated: horse.castrated || false,
      cryptorchid: horse.cryptorchid || false,
      horse_type_male: horse.horse_type_male || '',
      horse_type_female: horse.horse_type_female || '',
      bred_and_raised: horse.bred_and_raised || false,
      engagements_breeders_cup: horse.engagements_breeders_cup || false,
      engagements_ntra: horse.engagements_ntra || false,
      engagements_ebf: horse.engagements_ebf || false,
      engagements_ktdf: horse.engagements_ktdf || false,
    });
    setIsEditing(true);
  };

  const handleSave = async () => {
    if (!form.birthdate) { toast.error('Birthdate is required'); return; }
    setSaving(true);
    try {
      const cleanedData = { ...form };
      const nullIfEmpty = ['color', 'area_foaled', 'microchip_id', 'horse_type_male',
        'horse_type_female', 'name', 'sire_name', 'dam_name', 'jockey_club_registration'];
      nullIfEmpty.forEach(key => { if (cleanedData[key] === '') cleanedData[key] = null; });
      if (cleanedData.dam_year_foaled === '' || cleanedData.dam_year_foaled === null) cleanedData.dam_year_foaled = null;
      else if (cleanedData.dam_year_foaled) cleanedData.dam_year_foaled = parseInt(cleanedData.dam_year_foaled);
      await horseAPI.update(horse.id, cleanedData);
      toast.success('Basic details updated');
      setIsEditing(false);
      onUpdate?.();
    } catch {
      toast.error('Failed to update');
    } finally {
      setSaving(false);
    }
  };

  const f = (key) => form[key] ?? '';
  const set = (key, val) => setForm(prev => ({ ...prev, [key]: val }));

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between pb-3">
        <CardTitle className="text-base">Basic Details</CardTitle>
        {canEditAccount && !isEditing && (
          <Button variant="ghost" size="icon" onClick={startEditing} data-testid="edit-basic-details-btn">
            <Pencil className="w-4 h-4" />
          </Button>
        )}
      </CardHeader>
      <CardContent>
        {isEditing ? (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Name</label>
                <Input value={f('name')} onChange={e => set('name', e.target.value)} placeholder="Horse name" />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Birthdate</label>
                <Input type="date" value={f('birthdate')} onChange={e => set('birthdate', e.target.value)} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Jockey Club Registration #</label>
                <Input value={f('jockey_club_registration')} onChange={e => set('jockey_club_registration', e.target.value)} placeholder="Registration number" />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Sex</label>
                <Select value={f('sex')} onValueChange={v => set('sex', v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="male">Male</SelectItem>
                    <SelectItem value="female">Female</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Color</label>
                <Select value={f('color')} onValueChange={v => set('color', v)}>
                  <SelectTrigger><SelectValue placeholder="Select color" /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(COLOR_LABELS).map(([val, lbl]) => <SelectItem key={val} value={val}>{lbl}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Area Foaled</label>
                <Select value={f('area_foaled') || ''} onValueChange={v => set('area_foaled', v)}>
                  <SelectTrigger><SelectValue placeholder="Select area foaled" /></SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      <SelectLabel>United States</SelectLabel>
                      {['US','Arizona','California','Delaware','Florida','Illinois','Indiana','Kentucky','Louisiana','Maryland','Massachusetts','Minnesota','Montana','New Jersey','New Mexico','New York','North Carolina','Ohio','Oregon','Pennsylvania','Puerto Rico','Texas','Virginia','West Virginia','Wyoming'].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectGroup>
                    <SelectGroup>
                      <SelectLabel>Canada</SelectLabel>
                      {['Canada','Alberta','British Columbia','Ontario','Saskatchewan'].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectGroup>
                    <SelectGroup>
                      <SelectLabel>International</SelectLabel>
                      {['Australia','France','Ireland','Japan','UAE','Other'].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectGroup>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Microchip ID</label>
              <Input value={f('microchip_id')} onChange={e => set('microchip_id', e.target.value)} placeholder="Enter microchip ID" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Sire (Father)</label>
                <Input value={f('sire_name')} onChange={e => set('sire_name', e.target.value)} placeholder="Sire name" />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Dam (Mother)</label>
                <Input value={f('dam_name')} onChange={e => set('dam_name', e.target.value)} placeholder="Dam name" />
              </div>
            </div>

            {/* Foal status — if born this year */}
            {form.birthdate && new Date(`${form.birthdate}T12:00:00`).getFullYear() === new Date().getFullYear() && (
              <div className="border-t pt-4">
                <label className="text-sm font-medium mb-3 block">Foal Status</label>
                <div className="space-y-2">
                  {[['still_nursing','Still Nursing'], ['separated_from_dam','Separated from Dam']].map(([key, lbl]) => (
                    <label key={key} className="flex items-center gap-2">
                      <input type="checkbox" checked={!!form[key]} onChange={e => set(key, e.target.checked)} className="rounded accent-primary" />
                      <span className="text-sm">{lbl}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {/* Male status */}
            {form.sex === 'male' && (
              <div className="border-t pt-4">
                <label className="text-sm font-medium mb-3 block">Male Status</label>
                <div className="space-y-2">
                  {[['castrated','Castrated'], ['cryptorchid','Cryptorchid']].map(([key, lbl]) => (
                    <label key={key} className="flex items-center gap-2">
                      <input type="checkbox" checked={!!form[key]} onChange={e => set(key, e.target.checked)} className="rounded accent-primary" />
                      <span className="text-sm">{lbl}</span>
                    </label>
                  ))}
                </div>
                <div className="space-y-2 mt-3">
                  <label className="text-sm font-medium">Horse Type</label>
                  <Select value={f('horse_type_male')} onValueChange={v => set('horse_type_male', v)}>
                    <SelectTrigger><SelectValue placeholder="Select type" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="racing_stallion_prospect">Racing / Stallion Prospect</SelectItem>
                      <SelectItem value="stallion_prospect">Stallion Prospect</SelectItem>
                      <SelectItem value="stallion">Stallion</SelectItem>
                      <SelectItem value="not_applicable">Not Applicable</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}

            {/* Female status */}
            {form.sex === 'female' && (
              <div className="border-t pt-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Horse Type</label>
                  <Select value={f('horse_type_female')} onValueChange={v => set('horse_type_female', v)}>
                    <SelectTrigger><SelectValue placeholder="Select type" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="racing_broodmare_prospect">Racing/Broodmare Prospect</SelectItem>
                      <SelectItem value="broodmare_prospect">Broodmare Prospect</SelectItem>
                      <SelectItem value="broodmare">Broodmare</SelectItem>
                      <SelectItem value="maiden">Maiden</SelectItem>
                      <SelectItem value="not_applicable">Not Applicable</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}

            {/* Breeding & Engagements */}
            <div className="border-t pt-4">
              <label className="text-sm font-medium mb-3 block">Breeding &amp; Engagements</label>
              <div className="space-y-2">
                <label className="flex items-center gap-2">
                  <input type="checkbox" checked={!!form.bred_and_raised} onChange={e => set('bred_and_raised', e.target.checked)} className="rounded accent-primary" />
                  <span className="text-sm">Bred and/or Raised</span>
                </label>
              </div>
              <div className="mt-3">
                <p className="text-sm font-medium mb-2">Engagements:</p>
                <div className="space-y-2">
                  {[['engagements_breeders_cup',"Breeders' Cup"], ['engagements_ntra','NTRA'], ['engagements_ebf','EBF'], ['engagements_ktdf','KTDF']].map(([key, lbl]) => (
                    <label key={key} className="flex items-center gap-2">
                      <input type="checkbox" checked={!!form[key]} onChange={e => set(key, e.target.checked)} className="rounded accent-primary" />
                      <span className="text-sm">{lbl}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-muted-foreground text-xs uppercase">Sex</p>
              <p className="font-medium capitalize">{horse.sex}</p>
            </div>
            <div>
              <p className="text-muted-foreground text-xs uppercase">Color</p>
              <p className="font-medium">{COLOR_LABELS[horse.color] || horse.color?.replace(/_/g, ' ') || '-'}</p>
            </div>
            <div>
              <p className="text-muted-foreground text-xs uppercase">Born</p>
              <p className="font-medium">
                {horse.birthdate ? new Date(`${horse.birthdate.split('T')[0]}T12:00:00`).toLocaleDateString() : '-'}
              </p>
            </div>
            <div>
              <p className="text-muted-foreground text-xs uppercase">Area Foaled</p>
              <p className="font-medium">{horse.area_foaled || '-'}</p>
            </div>
            {horse.microchip_id?.trim() && (
              <div>
                <p className="text-muted-foreground text-xs uppercase">Microchip ID</p>
                <p className="font-medium font-mono text-xs">{horse.microchip_id}</p>
              </div>
            )}
            {((horse.sex === 'male' && horse.horse_type_male) || (horse.sex === 'female' && horse.horse_type_female)) && (
              <div>
                <p className="text-muted-foreground text-xs uppercase">Horse Type</p>
                <p className="font-medium capitalize text-xs">
                  {horse.sex === 'male' ? horse.horse_type_male.replace(/_/g, ' ') : horse.horse_type_female.replace(/_/g, ' ')}
                </p>
              </div>
            )}
            {horse.sex === 'female' && horse.cover_status && (
              <div className="col-span-2">
                <p className="text-muted-foreground text-xs uppercase">Cover Status</p>
                <Badge variant="outline" className="text-xs capitalize">{horse.cover_status.replace('_', ' ')}</Badge>
              </div>
            )}
          </div>
        )}
        {isEditing && (
          <div className="flex justify-end gap-2 mt-4 pt-4 border-t">
            <Button variant="outline" size="sm" onClick={() => setIsEditing(false)} disabled={saving}>Cancel</Button>
            <Button size="sm" onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : 'Save'}</Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default HorseBasicDetailsCard;
